import { API_ROUTES } from '@/constants/apiRoutes';
import {
  BankApplicationAccountType,
  BankApplicationDetails,
  BankApplicationFormValues,
  BankApplicationListResponse,
  PendingActionsFormFields,
} from '@/types/bankApplications';
import { IdempotentRequest } from '@/types/common';
import { api, formatCurrencyForPost } from '@/utils/api';

export const getBankApplicationsList = async (queryParams: string) => {
  const { data } = await api<BankApplicationListResponse>({
    url: `${API_ROUTES.BANK_APPLICATIONS}?${queryParams}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const getBankApplicationDetails = async (bankApplicationId: string) => {
  const { data } = await api<BankApplicationDetails>({
    url: `${API_ROUTES.BANK_APPLICATIONS}${bankApplicationId}`,
    method: 'get',
    version: 2,
  });

  return data;
};

export const createBankApplication = async (
  payload: IdempotentRequest<BankApplicationFormValues>,
) => {
  let applicantIds = {};
  if (payload.accountType === BankApplicationAccountType.BUSINESS) {
    applicantIds = {
      owners: payload.jointOwnerIds.map(owner => {
        return {
          consumer_id: owner,
          is_primary_owner: owner === payload.primaryOwner,
        };
      }),
      business_id: payload.businessId,
    };
  } else {
    applicantIds = {
      owners:
        payload.accountType === BankApplicationAccountType.INDIVIDUAL
          ? [
              {
                consumer_id: payload.individualOwnerId,
                is_primary_owner: true,
              },
            ]
          : payload.jointOwnerIds.map(owner => {
              return {
                consumer_id: owner,
                is_primary_owner: owner === payload.primaryOwner,
              };
            }),
    };
  }
  const { data } = await api<BankApplicationDetails>({
    url: API_ROUTES.BANK_APPLICATIONS,
    method: 'post',
    version: 2,
    data: {
      account_type: payload.accountType,
      advisor_id: payload.advisorId,
      ...applicantIds,
    },
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};

export const updateBankApplication = async (
  payload: IdempotentRequest<BankApplicationFormValues>,
) => {
  let applicantIds = {};
  if (payload.accountType === BankApplicationAccountType.BUSINESS) {
    applicantIds = {
      owners: payload.jointOwnerIds.map(owner => {
        return {
          consumer_id: owner,
          is_primary_owner: owner === payload.primaryOwner,
        };
      }),
      business_id: payload.businessId,
    };
  } else {
    applicantIds = {
      owners:
        payload.accountType === BankApplicationAccountType.INDIVIDUAL
          ? [
              {
                consumer_id: payload.individualOwnerId,
                is_primary_owner: true,
              },
            ]
          : payload.jointOwnerIds.map(owner => {
              return {
                consumer_id: owner,
                is_primary_owner: owner === payload.primaryOwner,
              };
            }),
    };
  }
  const { data } = await api<BankApplicationDetails>({
    url: `${API_ROUTES.BANK_APPLICATIONS}${payload.applicationId}`,
    method: 'put',
    version: 2,
    data: {
      ...applicantIds,
      withdraw_from_id: payload.externalAccountDepositId || null,
      deposit_amount: payload.depositAmount
        ? formatCurrencyForPost(payload.depositAmount)
        : null,
      beneficiaries: payload.beneficiaries.map(beneficiary => {
        return {
          first_name: beneficiary.firstName,
          last_name: beneficiary.lastName,
          tin: beneficiary.tin,
          percent_of_allocation: beneficiary.percentOfAllocation,
        };
      }),
    },
    idempotencyKey: payload.idempotencyKey,
  });

  return data;
};

export const submitBankApplication = async (bankApplicationId: string) => {
  const { data } = await api<BankApplicationDetails>({
    url: `${API_ROUTES.BANK_APPLICATIONS}${bankApplicationId}/submit`,
    method: 'post',
    version: 2,
  });

  return data;
};

export const deleteBankApplication = async (bankApplicationId: string) => {
  const { data } = await api<BankApplicationDetails>({
    url: `${API_ROUTES.BANK_APPLICATIONS}${bankApplicationId}`,
    method: 'delete',
    version: 2,
  });

  return data;
};

export const addPendingActionsService = async (
  payload: PendingActionsFormFields,
) => {
  const data = [];
  if (payload.businessActions.length > 0) {
    data.push({
      business_id: payload.businessId || '',
      actions: payload.businessActions.map(a => a.value),
    });
  }

  if (payload.ownerActions.length > 0) {
    data.push({
      owner_id: payload.ownerId || '',
      actions: payload.ownerActions.map(a => a.value),
    });
  }

  const { status } = await api({
    url: `${API_ROUTES.BANK_APPLICATIONS}${payload.id}/pending-actions`,
    method: 'post',
    version: 2,
    data,
  });

  if (status === 200) {
    await api({
      url: `${API_ROUTES.BANK_APPLICATIONS}${payload.id}/notify-pending-actions`,
      method: 'post',
      version: 2,
    });
  }

  return status;
};

export const verifyApplicationExternalAccountsService = async (
  bankApplicationId: string,
) => {
  const { data } = await api<BankApplicationDetails>({
    url: `${API_ROUTES.BANK_APPLICATIONS}${bankApplicationId}/verify-external-accounts`,
    method: 'post',
    version: 2,
  });

  return data;
};
